import React, {
    ChangeEvent,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import axios, { AxiosError } from 'axios';

import {
    Box,
    VStack,
    HStack,
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Textarea,
    useBoolean,
    Text,
    UnorderedList,
    ListItem,
} from '@chakra-ui/react';

import PageTitle from '../../components/PageTitle';
import ToastBox from '../../components/ToastBox';
import { ToastBoxContext } from '../../contexts/ToastBoxContext';
import apis from '../../utils/apis';
import Loading from '../../components/Loading';

export interface Scoping {
    id?: string;
    productName: string;
    productVision: string;
    knownSustainabilityEffects: string[]
}

function Scoping() {
    const {
        toast,
        showToast,
        onCloseToast,
    } = useContext(ToastBoxContext);

    const [productName, setProductName] = useState<string>('');
    const [productVision, setProductVision] = useState<string>('');
    const [knownSustainabilityEffects, setKnownSustainabilityEffects] = useState<string>('');

    const [productNameError, setProductNameError] = useState('');
    const [productVisionError, setProductVisionError] = useState('');
    const [knownSustainabilityEffectsError, setKnownSustainabilityEffectsError] = useState('');

    const handleProductNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        if (productNameError) {
            setProductNameError('');
            return;
        }
        setProductName(e.target.value);
    }, [productNameError]);

    const handleProductVisionChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
        if (productVisionError) {
            setProductVisionError('');
            return;
        }
        setProductVision(e.target.value);
    }, [productVisionError]);

    const handleKnownSustainabilityEffectsChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            if (knownSustainabilityEffectsError) {
                setKnownSustainabilityEffectsError('');
                return;
            }
            setKnownSustainabilityEffects(e.target.value);
        },
        [knownSustainabilityEffectsError],
    );

    useEffect(
        () => {
            if (toast) {
                onCloseToast();
            }
        },
        [onCloseToast, toast],
    );

    const onSaveScoping = useCallback(
        async (formData: Scoping) => {
            try {
                const requestBody = formData;
                const apiUrl = 'susaf/scoping/add';
                const response = await apis.post(apiUrl, requestBody);
                if (response.status === 200) {
                    const responseData: Scoping = response.data;

                    const successToastComponent = responseData && toast && toast({
                        status: 'success',
                        isClosable: true,
                        variant: 'subtle',
                        id: undefined,
                        duration: null,
                        position: 'top',
                        render: () => (
                            <ToastBox
                                onCloseToast={onCloseToast}
                                title="Product scoping added"
                                description="Product scoping has been added successfully"
                                status="success"
                            />
                        ),
                    });
                    showToast(successToastComponent);
                }
            } catch (error) {
                if (!axios.isAxiosError(error)) {
                    return;
                }
                const axiosError = error as AxiosError;
                const dataError = axiosError?.response?.data;
                console.warn({ dataError });
                const failureToastComponent = toast && toast({
                    status: 'error',
                    isClosable: true,
                    variant: 'subtle',
                    id: undefined,
                    duration: null,
                    position: 'top',
                    render: () => (
                        <ToastBox
                            onCloseToast={onCloseToast}
                            title="Error adding"
                            description="Could not add. Try again"
                            status="error"
                        />
                    ),
                });

                showToast(failureToastComponent);
            }
        },
        [onCloseToast, showToast, toast],
    );

    const onClearForm = useCallback(
        () => {
            setProductName('');
            setProductVision('');
            setKnownSustainabilityEffects('');
        },
        [],
    );

    const handleSubmit = useCallback(
        (event) => {
            event.preventDefault();
            if (!productName) {
                setProductNameError('Please enter product name');
                return;
            }
            if (!productVision) {
                setProductVisionError('Please enter product vision');
                return;
            }
            if (!knownSustainabilityEffects) {
                setProductVisionError('Please enter product vision');
                return;
            }

            const knownSustainabilityEffectsArray = knownSustainabilityEffects.split(',');

            onSaveScoping({
                productName,
                productVision,
                knownSustainabilityEffects: knownSustainabilityEffectsArray,
            } as Scoping);
            onClearForm();
        },
        [knownSustainabilityEffects, onClearForm, onSaveScoping, productName, productVision],
    );

    const [loadingScoping, setLoadingScoping] = useBoolean();
    const [scopingList, setScopingList] = useState<Scoping[]>();

    const getSavedScopings = useCallback(
        async () => {
            try {
                setLoadingScoping.on();
                const apiUrl = '/susaf/scoping/get/all';
                const response = await apis.get(apiUrl);
                const dataResponse: Scoping[] = await response.data;
                if (dataResponse.length <= 0) {
                    setLoadingScoping.off();
                    return;
                }
                setScopingList(dataResponse);
                setLoadingScoping.off();
            } catch (error) {
                // TODO: HANDLE THIS ERROR

                console.warn({ error });
            }
        },
        [setLoadingScoping],
    );

    useEffect(() => {
        getSavedScopings();
    }, [getSavedScopings]);

    return (
        <VStack>
            <PageTitle
                title="Warm-up: Scoping"
            />
            <Box
                width="100%"
                alignItems="flex-start"
                background="#fff"
            >
                <form onSubmit={handleSubmit}>
                    <VStack
                        spacing={4}
                        borderWidth={1}
                        padding={4}
                        width="100%"
                    >
                        <FormControl isInvalid={!!productNameError}>
                            <FormLabel
                                htmlFor="productName"
                            >
                                Product Name *
                            </FormLabel>
                            <Input
                                id="productName"
                                value={productName}
                                onChange={handleProductNameChange}
                                placeholder="Product Name"
                                background="whiteAlpha.900"
                                isRequired
                                autoComplete="off"
                                minHeight={12}
                            />
                            {productNameError && (
                                <FormErrorMessage>
                                    {productNameError}
                                </FormErrorMessage>
                            )}
                        </FormControl>
                        <FormControl>
                            <FormLabel htmlFor="productVision">
                                Outcome: Product or Service Vision
                            </FormLabel>
                            <Textarea
                                id="productVision"
                                value={productVision}
                                onChange={handleProductVisionChange}
                                placeholder="Enter product or service vision"
                                rows={3}
                                autoComplete="off"
                            />
                        </FormControl>
                        <FormControl isInvalid={!!knownSustainabilityEffectsError}>
                            <FormLabel
                                htmlFor="knownSustainabilityEffects"
                            >
                                Outcome: Known sustainability effects
                            </FormLabel>
                            <Input
                                id="knownSustainabilityEffects"
                                value={knownSustainabilityEffects}
                                onChange={handleKnownSustainabilityEffectsChange}
                                placeholder="Please put comma separated values"
                                background="whiteAlpha.900"
                                isRequired
                                autoComplete="off"
                                minHeight={12}
                            />
                            {knownSustainabilityEffectsError && (
                                <FormErrorMessage>
                                    {knownSustainabilityEffectsError}
                                </FormErrorMessage>
                            )}
                        </FormControl>
                        <HStack
                            width="100%"
                            justifyContent="flex-end"
                            spacing={4}
                        >
                            <Button
                                type="reset"
                                colorScheme="brand"
                                variant="outline"
                                letterSpacing={1}
                                onClick={onClearForm}
                                py={4}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                letterSpacing={1}
                                colorScheme="brand"
                                py={4}
                            >
                                Submit
                            </Button>
                        </HStack>
                    </VStack>
                </form>
            </Box>
            {scopingList && scopingList.length > 0 && (
                <Box
                    width="100%"
                    alignItems="flex-start"
                    background="#fff"
                >
                    <Text
                        fontSize="x-large"
                        fontWeight="bold"
                        zIndex={999}
                        textTransform="capitalize"
                        marginTop={12}
                    >
                        Your Scoping list
                    </Text>
                    {scopingList.map(
                        (item) => (
                            <Box
                                key={item.id}
                                borderWidth={1}
                                width="100%"
                                padding={4}
                                marginTop={4}
                            >
                                <Text>
                                    <span
                                        style={{
                                            fontWeight: 'bold',
                                            fontSize: 16,
                                            textTransform: 'capitalize',
                                        }}
                                    >
                                        Product Name:
                                        {' '}
                                        {`${item.productName} `}
                                    </span>
                                    <br />
                                    <span style={{ fontSize: 16, textTransform: 'capitalize' }}>
                                        Product Vision:
                                        {' '}
                                        {item.productVision}
                                    </span>
                                    <br />

                                </Text>
                                {item.knownSustainabilityEffects && (
                                    <>
                                        <Text
                                            style={{
                                                fontSize: 16,
                                                textTransform: 'capitalize',
                                            }}
                                        >
                                            <em>
                                                Known Sustainability Effects
                                            </em>
                                        </Text>
                                        <UnorderedList
                                            paddingX={4}
                                            paddingY={2}
                                        >
                                            {item.knownSustainabilityEffects.map((effect) => (
                                                <ListItem key={effect}>
                                                    {effect}
                                                </ListItem>
                                            ))}
                                        </UnorderedList>
                                    </>
                                )}
                            </Box>
                        ),
                    )}
                </Box>
            )}
            {loadingScoping && (
                <Box width="100%">
                    <Loading message="Loading scoping list" />
                </Box>
            )}
        </VStack>
    );
}

export default Scoping;
